import apis from "@api/api";
import DefaultScreen from "@components/Global/DefaultScreenView";
import styles from "@stylesheet";
import React, { useCallback, useMemo, memo, useState } from 'react';
import { Pressable, Platform, RefreshControl, View, FlatList, ScrollView, TouchableOpacity, Text as RNText, Image } from "react-native";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { H1, SizableText, XStack, H2, H4, Button, H6 } from "tamagui";
import AnimatedLottieView from 'lottie-react-native';
import { Text } from "tamagui";
import { MaterialIcons } from '@expo/vector-icons';
import FeedComponent from "../WelcomeScreen/Widgets/Components/FeedComponent";
import moment from "moment";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import { AntDesign, FontAwesome } from "@expo/vector-icons";
import { DefaultRootState } from "@reduxLocal/persistState";
import { useSelector } from "react-redux";
import * as Animatable from 'react-native-animatable';
import { useNavigation } from '@react-navigation/native';
import { selectUser } from "@reduxLocal/selectors/userSelectors";
import SettingsTab from './SalesComponents/SettingsTab';
import DashboardTab from './SalesComponents/DashboardTab';
import HistoryTab from './SalesComponents/HistoryTab';
import ScheduleTab from './SalesComponents/ScheduleTab';
import AnalyticsTab from './SalesComponents/AnalyticsTab';
import DesignerTab from './SalesComponents/DesignerTab';
import CompetitorAnalysisTab from './SalesComponents/CompetitorAnalysisTab';
// TypeScript interfaces for better type safety
export interface FeedItemProps {
  item: any;
  index: number;
  theme: boolean;
  globalDate: string;
  refetch: () => void;
  onPostSelect: (postId: string) => void;
  isSelected: boolean;
}

export interface EmptyComponentProps {
  isLoading: boolean;
  isFetching: boolean;
}

// Memoized FeedItem component to prevent unnecessary re-renders
export const MemoizedFeedItem = memo<FeedItemProps>(({ item, index, theme, globalDate, refetch, onPostSelect, isSelected }) => {
  return (
    <Pressable
      onPress={() => onPostSelect(item.id || item._id)}
      style={{
        flex: 1,
        backgroundColor: theme ? 'white' : 'black',
        paddingBottom: 20,
        borderWidth: isSelected ? 2 : 0,
        borderColor: isSelected ? (theme ? '#007AFF' : '#64b5f6') : 'transparent',
        borderRadius: isSelected ? 12 : 20,
        marginHorizontal: isSelected ? 8 : 0,
        opacity: isSelected ? 0.9 : 1,
      }}
    >
      <FeedComponent
        hideToolbar
        refresh={refetch}
        horizontalPadding={0}
        index={index}
        forceHorizontal={false}
        item={item}
      />
      <SizableText size={10} style={{ textAlign: 'center', marginVertical: 10 }}>
        Posted {moment(item.createdAt).format(globalDate)}
      </SizableText>
      <XStack gap={20} style={{ height: 50, paddingHorizontal: 30, alignSelf: "stretch" }}>
        <View style={[styles.centerEverything, { flex: 1 }]}>
          <AntDesign style={{ flex: 1, alignSelf: "center" }} name="eye" size={20} color={theme ? "black" : "white"} />
          <SizableText>{item.viewCount || 0}</SizableText>
        </View>
        <View style={[styles.centerEverything, { flex: 1 }]}>
          <AntDesign style={{ flex: 1, alignSelf: "center" }} name="like" size={20} color={theme ? "black" : "white"} />
          <SizableText>{item.likes?.length || 0}</SizableText>
        </View>
        <View style={[styles.centerEverything, { flex: 1 }]}>
          <FontAwesome style={{ flex: 1, alignSelf: "center" }} name="comments" size={20} color={theme ? "black" : "white"} />
          <SizableText>{item.comments?.length || 0}</SizableText>
        </View>
        <View style={[styles.centerEverything, { flex: 1 }]}>
          <FontAwesome style={{ flex: 1, alignSelf: "center" }} name="share-alt" size={20} color={theme ? "black" : "white"} />
          <SizableText>{item.shares?.length || 0}</SizableText>
        </View>
      </XStack>

      {/* Selection indicator */}
      {isSelected && (
        <View style={{
          position: 'absolute',
          top: 8,
          right: 8,
          backgroundColor: theme ? '#007AFF' : '#64b5f6',
          borderRadius: 12,
          padding: 6,
        }}>
          <MaterialIcons name="edit" size={16} color="white" />
        </View>
      )}
    </Pressable>
  );
});// Set display name for debugging
MemoizedFeedItem.displayName = 'MemoizedFeedItem';

// Memoized Empty Component
export const MemoizedEmptyComponent = memo<EmptyComponentProps>(({ isLoading, isFetching }) => {
  const theme = themeSelector();

  // Don't show empty state while loading
  if (isLoading || isFetching) {
    return null;
  }

  return (
    <View style={[styles.centerEverything, {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'transparent',
      gap: 20,
      paddingVertical: 60,
      paddingHorizontal: 20
    }]}>
      {Platform.OS !== 'android' && (
        <AnimatedLottieView
          source={require('@assets/animations/magnifExample.json')}
          style={{ flex: 1, maxHeight: 250, alignSelf: 'center' }}
          autoPlay
          loop
        />
      )}
      <MaterialIcons
        name="photo-library"
        size={64}
        color={theme ? "#64748b" : "#94a3b8"}
        style={{ marginBottom: 8 }}
      />
      <H4 style={{
        textAlign: 'center',
        color: theme ? "#1e293b" : "#f1f5f9",
        fontWeight: '700',
        marginBottom: 8
      }}>
        No Posts Yet
      </H4>
      <SizableText
        style={{
          textAlign: 'center',
          paddingHorizontal: 20,
          color: theme ? "#64748b" : "#94a3b8",
          fontSize: 14
        }}
      >
        Create your first social media post to get started!
      </SizableText>
    </View>
  );
});
MemoizedEmptyComponent.displayName = 'MemoizedEmptyComponent';

// Set display name for debugging

// Tab type definition
type TabKey = 'dashboard' | 'history' | 'schedule' | 'analytics' | 'designer' | 'settings' | 'competitors';

// Tab configuration
const tabs: { key: TabKey; title: string; icon: string }[] = [
  { key: 'dashboard', title: 'Dashboard', icon: 'dashboard' },
  { key: 'history', title: 'History', icon: 'history' },
  { key: 'schedule', title: 'Schedule', icon: 'schedule' },
  { key: 'analytics', title: 'Analytics', icon: 'analytics' },
  { key: 'designer', title: 'Designer', icon: 'format-paint' },
  { key: 'competitors', title: 'Competitors', icon: 'business' },
  { key: 'settings', title: 'Settings', icon: 'settings' }
];

// --- Memoized HeaderComponent ---
interface HeaderComponentProps {
  totalPosts: number;
  theme: boolean;
  handleCreateNewPost: () => void;
  navigation: any;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}
const HeaderComponent = React.memo(({ totalPosts, theme, handleCreateNewPost, navigation, isCollapsed, onToggleCollapse }: HeaderComponentProps) => {
  return (
    <Animatable.View
      animation="fadeInDown"
      duration={800}
      style={{
        backgroundColor: theme ? "#ffffff" : "#1e293b",
        borderRadius: 20,
        padding: 24,
        marginVertical: 16,
        marginHorizontal: 20,
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: theme ? 0.1 : 0.3,
        shadowRadius: 20,
        elevation: 10,
        borderWidth: 1,
        borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
      }}
    >
      <View style={{ flexWrap: 'wrap', flexDirection: 'row', alignItems: 'center', marginBottom: isCollapsed ? 0 : 20, justifyContent: 'space-between' }}>
        <View style={{ flexWrap: 'wrap', flexDirection: 'row', alignItems: 'center', flex: 1 }}>
          <View style={{
            backgroundColor: theme ? "#fff3e0" : "#e65100",
            borderRadius: 12,
            padding: 12,
            marginRight: 16
          }}>
            <MaterialIcons
              name="camera-alt"
              size={28}
              color={theme ? "#f57c00" : "#ffcc02"}
            />
          </View>
          <View style={{ flex: 1 }}>
            <H6 style={{
              color: theme ? "#1e293b" : "#f1f5f9",
              fontWeight: '700',
              marginBottom: 4
            }}>
              Social Media Studio
            </H6>
            {!isCollapsed && (
              <SizableText style={{
                color: theme ? "#64748b" : "#94a3b8",
                fontSize: 14
              }}>
                Create and manage your social media content
              </SizableText>
            )}
          </View>
        </View>
        <Pressable
          onPress={onToggleCollapse}
          style={{
            backgroundColor: theme ? "#f0f0f0" : "#2d3748",
            borderRadius: 20,
            padding: 8,
            marginLeft: 12
          }}
        >
          <MaterialIcons
            name={isCollapsed ? "expand-more" : "expand-less"}
            size={24}
            color={theme ? "#1e293b" : "#f1f5f9"}
          />
        </Pressable>
      </View>

      {/* Stats Grid */}
      {!isCollapsed && (
        <Animatable.View
          animation="fadeInDown"
          duration={300}
        >
          <XStack gap={8} flexWrap="wrap" justifyContent="center" alignItems="center">
            {[
              {
                title: 'Total Posts',
                value: totalPosts.toString(),
                icon: 'photo-library',
                color: theme ? "#f57c00" : "#ffcc02",
                bgColor: theme ? "#fff3e0" : "#e65100"
              },
              {
                title: 'Drafts',
                value: '0',
                icon: 'edit',
                color: theme ? "#9c27b0" : "#ce93d8",
                bgColor: theme ? "#f3e5f5" : "#4a148c"
              },
              {
                title: 'Published',
                value: totalPosts.toString(),
                icon: 'publish',
                color: theme ? "#4caf50" : "#81c784",
                bgColor: theme ? "#e8f5e8" : "#1b5e20"
              },
              {
                title: 'Engagement',
                value: '92%',
                icon: 'trending-up',
                color: theme ? "#2196f3" : "#64b5f6",
                bgColor: theme ? "#e3f2fd" : "#0d47a1"
              },
              {
                title: 'Reach',
                value: '2.5K',
                icon: 'visibility',
                color: theme ? "#ff5722" : "#ff8a65",
                bgColor: theme ? "#fbe9e7" : "#bf360c"
              }
            ].map((stat, index) => (
              <Animatable.View
                key={index}
                animation="bounceIn"
                delay={index * 200}
                style={{
                  flexBasis: Platform.OS === 'web' ? '18%' : '30%',
                  flexGrow: 1,
                  minWidth: 120,
                  maxWidth: Platform.OS === 'web' ? 180 : undefined,
                  backgroundColor: stat.bgColor,
                  borderRadius: 16,
                  padding: 16,
                  alignItems: 'center',
                  marginBottom: 8,
                  borderWidth: 1,
                  borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
                }}
              >
                <MaterialIcons
                  name={stat.icon as any}
                  size={28}
                  color={stat.color}
                  style={{ marginBottom: 8 }}
                />
                <Text style={{
                  fontSize: 20,
                  fontWeight: '800',
                  color: stat.color,
                  marginBottom: 4
                }}>
                  {stat.value}
                </Text>
                <Text style={{
                  fontSize: 11,
                  fontWeight: '600',
                  color: theme ? "#64748b" : "#94a3b8",
                  textAlign: 'center'
                }}>
                  {stat.title}
                </Text>
              </Animatable.View>
            ))}
          </XStack>
        </Animatable.View>
      )}
    </Animatable.View>
  );
});

// Dashboard Tab Content Component - Overview/Stats

DashboardTab.displayName = 'DashboardTab';


HistoryTab.displayName = 'HistoryTab';

// Designer Tab Component - PostDesigner

DesignerTab.displayName = 'DesignerTab';
ScheduleTab.displayName = 'ScheduleTab';


AnalyticsTab.displayName = 'AnalyticsTab';

// Settings Tab Component


const SocialPosts = memo(() => {
  const theme = themeSelector();
  const navigation = useNavigation();
  const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat);
  const queryClient = useQueryClient();
  const user = selectUser();
  const userIntegrations = user?.authentications || {};
  const originalIntegrationData = queryClient.getQueryData(['integrationData']) as any[] || [];
  const [selectedPostData, setSelectedPostData] = useState(null);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('dashboard');
  const { data, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['userPosts'],
    queryFn: async () => {
      return await apis.getUserPosts().catch(() => []);
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true
  });
  const handlePostSelect = useCallback((postId) => {
    const item = data.find(p => (p.id || p._id) === postId);
    if (item) {
      // Prefer content, then description, then title
      const text = item.content || item.description || item.title || '';
      // Extract hashtags (words starting with #)
      const hashtags = Array.from(new Set((text.match(/#\w+/g) || []).map(tag => tag.replace('#', ''))));
      // Headline: first line or first sentence
      let headline = '';
      let caption = '';
      if (text.includes('\n')) {
        const [firstLine, ...rest] = text.split(/\n+/);
        headline = firstLine.trim();
        caption = rest.join(' ').trim();
      } else if (text.includes('.')) {
        const [firstSentence, ...rest] = text.split(/\.(.+)/);
        headline = (firstSentence || '').trim();
        caption = (rest.join('.').trim() || '').replace(/^\.+/, '');
      } else {
        headline = text.trim();
        caption = '';
      }
      // Remove hashtags from caption
      if (caption) {
        caption = caption.replace(/#\w+/g, '').replace(/\s+/g, ' ').trim();
      }
      // If item has explicit fields, prefer them
      const postData = {
        image: item.media?.[0]?.url || null,
        headline: item.title || headline,
        caption: item.content || item.description || caption,
        hashtags: item.hashtags && item.hashtags.length > 0 ? item.hashtags : hashtags,
        location: item.location || '',
        isSponsored: item.isSponsored || false,
      };
      setSelectedPostData(postData);
      setSelectedPostId(item.id || item._id);
      setIsCreatingNew(false);
    }
  }, [data]);
  const handleCreateNewPost = useCallback(() => {
    setSelectedPostData(null);
    setSelectedPostId(null);
    setIsCreatingNew(true);
  }, []);
  const handleResetPost = useCallback(() => {
    setSelectedPostData(null);
    setSelectedPostId(null);
    setIsCreatingNew(false);
  }, []);
  const refreshControl = (
    <RefreshControl
      refreshing={isFetching || isLoading}
      onRefresh={refetch}
    />
  );
  const handlePostSave = useCallback((postData) => {
    console.log('Save', postData);
  }, []);
  const handlePostPreview = useCallback((postData) => {
    console.log('Preview', postData);
  }, []);
  const handlePostPublish = useCallback((postData) => {
    console.log('Publish', postData);
  }, []);
  const handleToggleHeader = useCallback(() => {
    setIsHeaderCollapsed(prev => !prev);
  }, []);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <DashboardTab
            data={data || []}
            theme={theme}
            totalPosts={data?.length || 0}
          />
        );
      case 'history':
        return (
          <HistoryTab
            data={data || []}
            theme={theme}
            globalDate={globalDate}
            refetch={refetch}
            handlePostSelect={handlePostSelect}
            selectedPostId={selectedPostId}
            isFetching={isFetching}
            isLoading={isLoading}
            userIntegrations={userIntegrations}
            integrationData={originalIntegrationData}
          />
        );
      case 'schedule':
        return <ScheduleTab theme={theme} data={data || []} />;
      case 'analytics':
        return <AnalyticsTab theme={theme} data={data || []} />;
      case 'designer':
        return (
          <DesignerTab
            theme={theme}
            selectedPostData={selectedPostData}
            handlePostSave={handlePostSave}
            handlePostPreview={handlePostPreview}
            handlePostPublish={handlePostPublish}
          />
        );
      case 'competitors':
        return <CompetitorAnalysisTab theme={theme} />;
      case 'settings':
        return <SettingsTab theme={theme} />;
      default:
        return (
          <DashboardTab
            data={data || []}
            theme={theme}
            totalPosts={data?.length || 0}
          />
        );
    }
  };

  return (
    <DefaultScreen backButton scrollDisabled={Platform.OS === 'web'} style={{ paddingTop: 0, marginTop: 0 }} containerStyle={{ flex: 1, paddingTop: 0, marginTop: 0 }} >
      <View style={{ flex: 1 }}>
        {/* Tab Navigation */}
        <View style={{
          backgroundColor: theme ? '#ffffff' : '#1f2937',
          borderBottomWidth: 1,
          borderBottomColor: theme ? '#e5e7eb' : '#374151',
        }}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ paddingHorizontal: 20 }}
          >
            {tabs.map((tab) => (
              <TouchableOpacity
                key={tab.key}
                onPress={() => setActiveTab(tab.key)}
                style={{
                  paddingHorizontal: 16,
                  paddingVertical: 12,
                  marginRight: 8,
                  borderBottomWidth: 2,
                  borderBottomColor: activeTab === tab.key
                    ? '#3b82f6'
                    : 'transparent',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <MaterialIcons
                  name={tab.icon as any}
                  size={20}
                  color={activeTab === tab.key
                    ? '#3b82f6'
                    : theme ? '#6b7280' : '#9ca3af'
                  }
                />
                <RNText style={{
                  marginLeft: 8,
                  fontSize: 14,
                  fontWeight: activeTab === tab.key ? '600' : '400',
                  color: activeTab === tab.key
                    ? '#3b82f6'
                    : theme ? '#6b7280' : '#9ca3af'
                }}>
                  {tab.title}
                </RNText>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        {/* Header Component - only show on dashboard */}
        {activeTab === 'dashboard' && (
          <HeaderComponent
            totalPosts={data?.length || 0}
            theme={theme}
            handleCreateNewPost={handleCreateNewPost}
            navigation={navigation}
            isCollapsed={isHeaderCollapsed}
            onToggleCollapse={handleToggleHeader}
          />
        )}

        {/* Tab Content */}
        <View style={{ flex: 1 }}>
          {renderTabContent()}
        </View>
      </View>
    </DefaultScreen>
  );
});


export default SocialPosts;